import React from "react";
import { formatCurrency } from "./unitDetailHelpers";
import SectionCard from "./SectionCard";

/**
 * A current-state figure, or an em dash when the backend does not know one.
 *
 * `null` and `0` are different answers and must not render the same. The
 * run-rate contract returns `null` when a unit has no classified recurring
 * source -- "we know nothing" -- and `0.0` when it has one that sums to zero, a
 * measurement. Printing "CHF 0" for the first is the exact bug the units list
 * carried until it migrated: a confident zero the reader cannot tell apart from
 * a real one.
 */
function currentStateMoney(value) {
  return value == null ? "—" : formatCurrency(value);
}

/**
 * Why the headline and the cost rows below it may not add up.
 *
 * NOT A WARNING SURFACE. Each note states a fact about what the run-rate could
 * and could not count, in the same words the company-wide Betriebskosten card
 * already uses for the same situation. A reader who sees a "Kaution" row in the
 * table and a headline that does not include it needs an answer here, not a
 * badge; if there is nothing to say, this renders nothing at all.
 *
 * AN ABSENT CONTRACT IS NOT A STATEMENT ABOUT THE DATA, and the guard on the
 * first line is what keeps it from becoming one. The detail read is fail-closed:
 * when the running-costs aggregator raises, all three contract fields come back
 * `null` and the response still succeeds. That says the SUMMARY is unavailable.
 * It says nothing about whether the unit has cost rows, and this component has
 * no way to tell that apart from any other reason the field is missing.
 *
 * `has_recurring_cost_basis: false` is the opposite: the contract WAS computed
 * and found no classified recurring source. Only that earns "Keine
 * klassifizierten laufenden Kosten erfasst." Folding the two together — by
 * defaulting `dataQuality` to an object, say — would put a claim about the
 * user's records on screen in precisely the case where the backend declined to
 * make one. The headline shows an em dash either way; what differs is whether
 * the page also explains it, and it may only explain what it knows.
 */
function RunningCostsDataQuality({ dataQuality }) {
  // See above: absent contract -> say nothing. Not a falsy-guard convenience.
  if (!dataQuality) return null;

  const unclassifiedCount = Number(dataQuality.unclassified_recurring_count) || 0;
  const unclassifiedTotal = Number(dataQuality.unclassified_recurring_total_chf) || 0;
  const unknownFrequency = Number(dataQuality.unknown_frequency_count) || 0;
  const premiumOnNonInsurance = dataQuality.premium_on_non_insurance_type === true;
  const hasBasis = dataQuality.has_recurring_cost_basis === true;

  const notes = [];

  if (unclassifiedCount > 0) {
    notes.push({
      key: "unclassified",
      amount: unclassifiedTotal,
      amountLabel: "Nicht klassifiziert",
      text:
        unclassifiedCount === 1
          ? "1 Kostenposition ohne erkannten Typ — nicht in den laufenden Kosten enthalten."
          : `${unclassifiedCount} Kostenpositionen ohne erkannten Typ — nicht in den laufenden Kosten enthalten.`,
    });
  }

  if (unknownFrequency > 0) {
    notes.push({
      key: "unknown-frequency",
      text:
        unknownFrequency === 1
          ? "1 Kostenposition mit unbekannter Frequenz — nicht in den laufenden Kosten enthalten."
          : `${unknownFrequency} Kostenpositionen mit unbekannter Frequenz — nicht in den laufenden Kosten enthalten.`,
    });
  }

  if (premiumOnNonInsurance) {
    notes.push({
      key: "premium-non-insurance",
      text:
        "Eine Versicherungsprämie ist erfasst, der Kautionstyp ist aber nicht «Versicherung» — die Prämie ist deshalb nicht eingerechnet.",
    });
  }

  // Only when nothing more specific explains the em dash. A unit whose single
  // cost row is unclassified already has its reason above; saying "nothing
  // recorded" on top of it would be wrong.
  if (!hasBasis && notes.length === 0) {
    notes.push({
      key: "no-basis",
      text: "Keine klassifizierten laufenden Kosten erfasst.",
    });
  }

  if (notes.length === 0) return null;

  return (
    <div className="mt-3 border-t border-black/10 dark:border-white/[0.05] pt-2 space-y-2">
      {notes.map((note) => (
        <div key={note.key}>
          {note.amount != null ? (
            <div className="flex items-center justify-between gap-3 text-[11px]">
              <span className="text-[#64748b] dark:text-[#6b7a9a]">{note.amountLabel}</span>
              <span className="font-medium tabular-nums text-[#0f172a] dark:text-[#eef2ff]">
                {formatCurrency(note.amount)}
              </span>
            </div>
          ) : null}
          <p className="mt-1 text-[10px] leading-snug text-[#64748b] dark:text-[#6b7a9a]">
            {note.text}
          </p>
        </div>
      ))}
    </div>
  );
}

export default function UnitFinanceDetails({
  kpiYear,
  kpiMonth,
  unitTenancyRevenueLoading,
  activeUnitTenancies,
  unitAggregatedRecurringBreakdown,
  unitAggregatedOneTimeTotal,
  unitAggregatedOneTimeBreakdown,
  totalExpendituresInfoMonth,
  currentRunningCostsMonthlyChf,
  currentRunningCostsBreakdown,
  currentRunningCostsDataQuality,
  oneTimeCostTotalDisplay,
  oneTimeCostBreakdownDisplay,
}) {
  return (
    <SectionCard
      title="Einnahmen & Kosten (Details)"
      subtitle="Monatliche und einmalige Einnahmen sowie Kosten im Detail (Backend-Aggregation)."
    >
      <div className="mt-4 rounded-[10px] border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 dark:border-green-500/20 dark:bg-green-500/5 dark:text-[#eef2ff]">
        <p className="font-semibold text-emerald-700 dark:text-[#4ade80] mb-2">
          Monatliche Einnahmen (Details)
        </p>
        <p className="text-xs text-emerald-800/90 dark:text-[#6b7a9a] mb-3">
          Wiederkehrende Einnahmen nach Typ (Monatsäquivalent), summiert über
          aktive Mietverhältnisse. «Aktueller Umsatz» oben zeigt dagegen den
          Umsatz für {String(kpiMonth).padStart(2, "0")}/{kpiYear} und kann davon
          abweichen.
        </p>
        {unitTenancyRevenueLoading ? (
          <p className="text-emerald-800/90 dark:text-[#6b7a9a]">…</p>
        ) : activeUnitTenancies.length === 0 ? (
          <p className="text-emerald-800/90 dark:text-[#6b7a9a]">Keine aktiven Mietverhältnisse.</p>
        ) : unitAggregatedRecurringBreakdown.length === 0 ? (
          <p className="text-emerald-800/90 dark:text-[#6b7a9a]">Keine Einnahmen definiert</p>
        ) : (
          <ul className="space-y-1">
            {unitAggregatedRecurringBreakdown.map((b) => (
              <li
                key={b.typeKey}
                className="flex justify-between gap-4 text-emerald-900 dark:text-[#eef2ff]"
              >
                <span>{b.label}</span>
                <span className="font-semibold tabular-nums text-emerald-600 dark:text-[#4ade80]">
                  {formatCurrency(b.total)}
                </span>
              </li>
            ))}
          </ul>
        )}
        {!unitTenancyRevenueLoading &&
        activeUnitTenancies.length > 0 &&
        unitAggregatedOneTimeTotal > 0 ? (
          <div className="mt-3 pt-3 border-t border-black/10 dark:border-white/[0.05]">
            <p className="text-xs font-semibold text-[#64748b] dark:text-[#6b7a9a] mb-2">
              Einmalige Einnahmen
            </p>
            <p className="text-sm text-emerald-900 dark:text-[#eef2ff] mb-2">
              Gesamt:{" "}
              <span className="font-semibold tabular-nums text-emerald-600 dark:text-[#4ade80]">
                {formatCurrency(unitAggregatedOneTimeTotal)}
              </span>
            </p>
            {unitAggregatedOneTimeBreakdown.length > 0 ? (
              <ul className="space-y-1 text-sm">
                {unitAggregatedOneTimeBreakdown.map((b) => (
                  <li
                    key={`ot-${b.typeKey}`}
                    className="flex justify-between gap-4 text-emerald-900 dark:text-[#eef2ff]"
                  >
                    <span>{b.label}</span>
                    <span className="font-semibold tabular-nums text-emerald-600 dark:text-[#4ade80]">
                      {formatCurrency(b.total)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="mt-4 rounded-xl border border-black/10 bg-slate-100 p-4 text-sm text-[#64748b] dark:border-white/[0.07] dark:bg-[#111520] dark:text-[#6b7a9a]">
        <p className="font-semibold text-[#64748b] dark:text-[#6b7a9a] mb-2">
          Monatliche Kosten (Details)
        </p>
        <p className="text-xs text-[#6b7a9a] mb-3">
          Aktuelle laufende Kosten: wiederkehrende Kostenpositionen und
          Kautionsversicherung (jährliche Prämie ÷ 12), ohne Zeitraumbezug.
          «Kosten im Monat» auf der Übersicht zeigt dagegen die Kosten für{" "}
          {String(kpiMonth).padStart(2, "0")}/{kpiYear}, tageweise auf die
          Mietdauer umgerechnet — beide Zahlen dürfen abweichen.
        </p>
        <div className="mb-3 pb-3 border-b border-black/10 dark:border-white/[0.05]">
          <p className="text-sm text-[#64748b] dark:text-[#6b7a9a]">
            <span className="text-[13px] font-medium text-[#0f172a] dark:text-[#eef2ff]">Gesamtausgaben (informativ):</span>{" "}
            <span className="font-semibold tabular-nums text-[#eef2ff]">
              {currentStateMoney(totalExpendituresInfoMonth)}
            </span>
          </p>
          <p className="text-xs text-[#6b7a9a] mt-1">
            Aktuelle laufende Kosten zuzüglich einmalige Kosten mit
            Erfassungsdatum in {String(kpiMonth).padStart(2, "0")}/{kpiYear}.
            Keine periodisierte Monatsabrechnung.
          </p>
        </div>
        <div className="mb-3">
          <p className="text-sm text-[#6b7a9a]">
            <span className="text-[#6b7a9a] font-medium">
              Laufende Kosten gesamt:
            </span>{" "}
            <span className="font-semibold tabular-nums text-[#eef2ff]">
              {currentStateMoney(currentRunningCostsMonthlyChf)}
            </span>
          </p>
          {currentRunningCostsBreakdown.length === 0 ? (
            <p className="text-[#6b7a9a] mt-2">Keine laufenden Kosten definiert</p>
          ) : (
            <ul className="space-y-1 mt-2">
              {currentRunningCostsBreakdown.map((b) => (
                <li
                  key={b.typeKey}
                  className="flex justify-between gap-4 text-[#6b7a9a]"
                >
                  <span>{b.label}</span>
                  <span className="font-medium tabular-nums text-[#eef2ff]">
                    {formatCurrency(b.total)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <RunningCostsDataQuality dataQuality={currentRunningCostsDataQuality} />
        </div>
        <div className="mt-3 pt-3 border-t border-black/10 dark:border-white/[0.07]">
          <p className="text-sm text-[#64748b] dark:text-[#6b7a9a] mb-2">
            <span className="text-[#64748b] dark:text-[#6b7a9a] font-medium">
              Einmalige Kosten gesamt:
            </span>{" "}
            <span className="font-semibold tabular-nums text-[#eef2ff]">
              {formatCurrency(oneTimeCostTotalDisplay)}
            </span>
          </p>
          <p className="text-xs text-[#6b7a9a] -mt-1 mb-2">
            Alle erfassten einmaligen Kosten — nicht nur die aus{" "}
            {String(kpiMonth).padStart(2, "0")}/{kpiYear}.
          </p>
          {oneTimeCostTotalDisplay <= 0 ? (
            <p className="text-[#6b7a9a]">Keine einmaligen Kosten</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {oneTimeCostBreakdownDisplay.map((b) => (
                <li
                  key={`otc-${b.typeKey}`}
                  className="flex justify-between gap-4 text-[#6b7a9a]"
                >
                  <span>{b.label}</span>
                  <span className="font-medium tabular-nums text-[#eef2ff]">
                    {formatCurrency(b.total)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </SectionCard>
  );
}
